/**
 * ProfileHeader — the visual centerpiece of the Profile page. An editorial,
 * author-page-style composition rather than a dense horizontal card: a soft
 * tone-gradient cover band, a large overlapping avatar, and generously
 * spaced identity content beneath it.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Link2, Calendar, Share2, Settings, UserPlus, UserCheck } from 'lucide-react';
import { Button, cx } from '@/components/site/primitives';
import { fadeUp } from '@/lib/motion';
import { toast } from '@/components/ui/sonner';
import { formatCount, formatJoinDate } from './utils/profileHelpers';
import ProfileBanner from './ProfileBanner';
import EditBannerModal from './EditBannerModal';
import { PROFILE } from '@/constants/testIds';

const SPINE_TONE = {
  coral: 'from-coral to-[#b85e58]',
  yellow: 'from-yellow to-[#e0b164]',
  sage: 'from-sage to-[#a9c39d]',
  blue: 'from-blue to-[#6b808b]',
};

export default function ProfileHeader({ profile, stats, isOwnProfile, isFollowing, onFollowToggle, onEdit, onUpdateBanner }) {
  const tone = SPINE_TONE[profile.tone] ? profile.tone : 'coral';
  const [isBannerEditOpen, setIsBannerEditOpen] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/app/profile/${profile.handle}`;
    try {
      await navigator.clipboard.writeText(url);
      toast('Profile link copied');
    } catch {
      toast(url);
    }
  };

  return (
    <header data-testid={PROFILE.header} className="relative">
      <ProfileBanner
        tone={tone}
        bannerImage={profile.bannerImage}
        isOwnProfile={isOwnProfile}
        onEdit={() => setIsBannerEditOpen(true)}
      />

      {isOwnProfile && (
        <EditBannerModal
          isOpen={isBannerEditOpen}
          onClose={() => setIsBannerEditOpen(false)}
          currentBanner={profile.bannerImage}
          tone={tone}
          onSave={onUpdateBanner}
          onRemove={() => onUpdateBanner(null)}
        />
      )}

      <div className="u-container">
        <div className="-mt-14 flex flex-col items-start gap-5 sm:-mt-16 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end">
            <motion.span
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className={cx(
                'grid h-28 w-28 shrink-0 place-items-center rounded-full bg-gradient-to-br font-display text-4xl font-bold text-[#FFF7F0] shadow-lift ring-4 ring-cream sm:h-32 sm:w-32',
                SPINE_TONE[tone]
              )}
            >
              {profile.initial}
            </motion.span>

            <div className="pb-1">
              <motion.h1 variants={fadeUp} initial="hidden" animate="show" className="font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">
                {profile.name}
              </motion.h1>
              <p className="mt-0.5 text-sm font-medium text-subtle">@{profile.handle}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 pb-1">
            <button
              type="button"
              onClick={handleShare}
              data-testid={PROFILE.shareButton}
              aria-label="Share profile"
              className="grid h-10 w-10 place-items-center rounded-full border border-line bg-card text-subtle transition-colors hover:border-coral/50 hover:text-coral"
            >
              <Share2 className="h-4 w-4" />
            </button>

            {isOwnProfile ? (
              <Button variant="secondary" icon={false} onClick={onEdit} data-testid={PROFILE.editButton} className="gap-1.5">
                <Settings className="h-4 w-4" /> Edit Profile
              </Button>
            ) : (
              <Button
                variant={isFollowing ? 'secondary' : 'primary'}
                icon={false}
                onClick={onFollowToggle}
                data-testid={PROFILE.followButton}
                className="gap-1.5"
              >
                {isFollowing ? <UserCheck className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
            )}
          </div>
        </div>

        {profile.bio && (
          <motion.p variants={fadeUp} custom={1} initial="hidden" animate="show" className="mt-5 max-w-xl font-display text-lg leading-relaxed text-ink/90 text-balance">
            &ldquo;{profile.bio}&rdquo;
          </motion.p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-subtle">
          {profile.location && (
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {profile.location}</span>
          )}
          {profile.website && (
            <span className="inline-flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> {profile.website}</span>
          )}
          {profile.joinedAt && (
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatJoinDate(profile.joinedAt)}</span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
          <span><span className="font-display font-semibold text-ink">{stats.booksRead}</span> <span className="text-subtle">Books</span></span>
          <span><span className="font-display font-semibold text-ink">{stats.scribbles}</span> <span className="text-subtle">Scribbles</span></span>
          <span><span className="font-display font-semibold text-ink">{formatCount(profile.followers)}</span> <span className="text-subtle">Followers</span></span>
          <span><span className="font-display font-semibold text-ink">{formatCount(profile.following)}</span> <span className="text-subtle">Following</span></span>
        </div>
      </div>
    </header>
  );
}
